import { VideoMetadataError, VideoMetadataErrorCode } from './video-metadata-error';

/**
 * 视频元数据接口
 */
export interface VideoMetadata {
  /** 视频宽度 */
  width: number;
  /** 视频高度 */
  height: number;
  /** 宽高比 */
  aspectRatio: number;
  /** 视频时长（秒） */
  duration: number;
  /** 封面图 URL（当 captureCoverImage 为 true 时返回，用于预览） */
  cover_url?: string;
  /** 封面图 Blob（当 captureCoverImage 为 true 时返回，用于上传） */
  cover_blob?: Blob;
}

/**
 * 获取视频元数据配置选项
 */
export interface GetVideoMetadataOptions {
  /** 超时时间（毫秒），默认为 10000ms */
  timeout?: number;
  /** 是否获取时长，默认为 true */
  includeDuration?: boolean;
  /** 是否截取视频首帧作为封面图，默认为 false */
  captureCoverImage?: boolean;
  /** 是否需要封面图的 Blob（用于上传），默认为 false。当为 true 时，会同时生成 cover_url 和 cover_blob */
  needCoverBlob?: boolean;
  /**
   * 视频元素的 crossOrigin 属性。仅在需要 `captureCoverImage` 跨域抓帧时设置为
   * `'anonymous'`，否则 canvas 会被污染导致 toDataURL/toBlob 抛错。
   */
  crossOrigin?: '' | 'anonymous' | 'use-credentials';
}

/**
 * 截取视频首帧图作为封面
 * @param video - HTML 视频元素
 * @param width - 视频宽度
 * @param height - 视频高度
 * @returns Canvas 元素，包含绘制好的封面图
 */
async function captureVideoFirstFrame({
  video,
  width,
  height,
}: {
  video: HTMLVideoElement;
  width: number;
  height: number;
}): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    video.currentTime = 0.1;
    video.addEventListener(
      'seeked',
      () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }

          // 将当前视频帧绘制到 canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          resolve(canvas);
        } catch {
          resolve(null);
        }
      },
      { once: true },
    );

    // 监听错误
    video.addEventListener(
      'error',
      () => {
        resolve(null);
      },
      { once: true },
    );
  });
}

/**
 * 获取视频的元数据信息（宽高、宽高比、时长）
 * @param videoUrl - 视频 URL
 * @param options - 配置选项
 * @returns Promise<VideoMetadata> 包含视频的宽高、宽高比和时长
 * @example
 * ```ts
 * const metadata = await getVideoMetadata('https://example.com/video.mp4');
 * console.log(metadata.width, metadata.height, metadata.aspectRatio);
 *
 * // 同时获取封面图
 * const metadataWithCover = await getVideoMetadata('https://example.com/video.mp4', { captureCoverImage: true });
 * console.log(metadataWithCover.cover_url);
 * ```
 */
export const getVideoMetadata = async (
  videoUrl: string,
  options: GetVideoMetadataOptions = {},
): Promise<VideoMetadata> => {
  const {
    timeout = 10000,
    includeDuration = true,
    captureCoverImage = false,
    needCoverBlob = false,
    crossOrigin,
  } = options;

  return new Promise<VideoMetadata>((resolve, reject) => {
    const video = document.createElement('video');
    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('error', handleError);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // 清理 video 元素
      video.src = '';
      video.load();
    };

    const handleLoadedMetadata = async () => {
      try {
        const width = video.videoWidth;
        const height = video.videoHeight;
        const duration = includeDuration ? video.duration : 0;

        if (!width || !height) {
          cleanup();
          reject(new VideoMetadataError(VideoMetadataErrorCode.METADATA_DIMENSIONS_FAILED, '无法获取视频尺寸'));
          return;
        }

        const aspectRatio = width / height;
        let cover_url = '';
        let cover_blob: Blob | undefined;

        // 如果需要截取封面图
        if (captureCoverImage) {
          const canvas = await captureVideoFirstFrame({ video, width, height });
          if (canvas) {
            // 总是生成预览 URL（用于显示）
            cover_url = canvas.toDataURL('image/jpeg', 0.9);

            // 只在需要时生成 Blob（用于上传）
            if (needCoverBlob) {
              cover_blob =
                (await new Promise<Blob | null>((resolve) => {
                  canvas.toBlob((blob) => resolve(blob || null), 'image/jpeg', 0.9);
                })) || undefined;
            }
          }
        }

        cleanup();
        resolve({
          width,
          height,
          aspectRatio,
          duration,
          ...(cover_url ? { cover_url } : {}),
          ...(cover_blob ? { cover_blob } : {}),
        });
      } catch (error) {
        cleanup();
        const errorMessage = error instanceof Error ? error.message : undefined;
        reject(
          new VideoMetadataError(
            VideoMetadataErrorCode.METADATA_FAILED,
            `获取视频元数据失败: ${errorMessage || '未知错误'}`,
            errorMessage,
          ),
        );
      }
    };

    const handleError = (event: Event) => {
      cleanup();
      reject(
        new VideoMetadataError(VideoMetadataErrorCode.METADATA_LOAD_FAILED, `视频加载失败: ${event.type}`, event.type),
      );
    };

    // 设置超时机制
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new VideoMetadataError(VideoMetadataErrorCode.METADATA_TIMEOUT, '获取视频元数据超时'));
    }, timeout);

    video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    video.addEventListener('error', handleError, { once: true });

    // 设置视频源并触发加载
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    if (crossOrigin !== undefined) {
      video.crossOrigin = crossOrigin;
    }
    video.src = videoUrl;
  });
};

/**
 * 从 HTMLVideoElement 获取视频元数据
 * @param video - HTML 视频元素
 * @param includeDuration - 是否包含时长，默认为 true
 * @returns VideoMetadata 包含视频的宽高、宽高比和时长
 * @example
 * ```ts
 * const video = document.querySelector('video');
 * const metadata = getVideoMetadataFromElement(video);
 * console.log(metadata.width, metadata.height, metadata.aspectRatio);
 * ```
 */
export const getVideoMetadataFromElement = (video: HTMLVideoElement, includeDuration = true): VideoMetadata | null => {
  try {
    const width = video.videoWidth;
    const height = video.videoHeight;
    const duration = includeDuration ? video.duration : 0;

    if (!width || !height) {
      return null;
    }

    const aspectRatio = width / height;

    return {
      width,
      height,
      aspectRatio,
      duration,
    };
  } catch (error) {
    console.error('获取视频元数据失败:', error);
    return null;
  }
};
